package com.nonopn.skywatcher.common.login.sub

import androidx.compose.desktop.ui.tooling.preview.Preview
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.IntrinsicSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.TextFieldValue
import androidx.compose.ui.unit.dp
import com.nonopn.skywatcher.common.ILoginCaller
import com.nonopn.skywatcher.common.login.LocalOpenSessionModel
import com.nonopn.skywatcher.common.login.pages.RouterMain
import com.nonopn.skywatcher.design.local.LocalConfirmPopup
import eu.codlab.blipya.common_sub_login.generated.resources.Res
import eu.codlab.blipya.common_sub_login.generated.resources.app_register_sub_title
import eu.codlab.blipya.common_sub_login.generated.resources.cancel
import eu.codlab.blipya.common_sub_login.generated.resources.create_account_title
import eu.codlab.blipya.common_sub_login.generated.resources.form_confirmation
import eu.codlab.blipya.common_sub_login.generated.resources.form_email
import eu.codlab.blipya.common_sub_login.generated.resources.form_password
import eu.codlab.blipya.common_sub_login.generated.resources.register_account_title
import eu.codlab.blipya.common_sub_login.generated.resources.unexpected_error
import eu.codlab.compose.widgets.CustomOutlinedButton
import eu.codlab.compose.widgets.CustomOutlinedEditText
import eu.codlab.compose.widgets.TextNormal
import eu.codlab.compose.widgets.TextSubtitle
import eu.codlab.compose.widgets.TextTitle
import eu.codlab.compose.widgets.preview.PreviewDarkLightColumn
import org.jetbrains.compose.resources.stringResource

@Suppress("UnusedPrivateMember", "LongMethod")
@Composable
fun CreateAccountScreen(
    loginCaller: ILoginCaller,
    modifier: Modifier = Modifier
) {
    val model = LocalOpenSessionModel.current

    var registerState by remember { mutableStateOf(RegisterState()) }
    var email by remember { mutableStateOf(TextFieldValue("")) }
    var password by remember { mutableStateOf(TextFieldValue("")) }
    var confirmation by remember { mutableStateOf(TextFieldValue("")) }

    val popup = LocalConfirmPopup.current
    val errorPopupTitle = stringResource(Res.string.unexpected_error)

    Column(modifier = modifier.padding(16.dp)) {
        TextTitle(
            text = stringResource(Res.string.create_account_title)
        )
        TextSubtitle(
            text = stringResource(Res.string.app_register_sub_title)
        )
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .weight(1f)
        ) {
            CustomOutlinedEditText(
                value = email,
                onValueChanged = {
                    registerState = registerState.newState(username = it.text)

                    email = email.copy(
                        text = it.text,
                        selection = it.selection
                    )
                },
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email),
                singleLine = true,
                label = {
                    TextNormal(
                        text = stringResource(Res.string.form_email)
                    )
                }
            )

            CustomOutlinedEditText(
                value = password,
                onValueChanged = {
                    registerState = registerState.newState(password = it.text)

                    password = password.copy(
                        text = it.text,
                        selection = it.selection
                    )
                },
                isError = !registerState.valid,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                singleLine = true,
                label = {
                    TextNormal(
                        text = stringResource(Res.string.form_password)
                    )
                }
            )

            CustomOutlinedEditText(
                value = confirmation,
                onValueChanged = {
                    registerState = registerState.newState(confirmation = it.text)

                    confirmation = confirmation.copy(
                        text = it.text,
                        selection = it.selection
                    )
                },
                isError = !registerState.valid,
                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Password),
                singleLine = true,
                label = {
                    TextNormal(
                        text = stringResource(Res.string.form_confirmation)
                    )
                }
            )
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.End
            ) {
                Column(
                    Modifier
                        .background(Color.Transparent)
                        .width(IntrinsicSize.Max),
                    Arrangement.spacedBy(5.dp)
                ) {
                    CustomOutlinedButton(
                        modifier = Modifier.fillMaxWidth(),
                        onClick = {
                            loginCaller.register(email.text, password.text) { err ->
                                if (null == err) return@register

                                popup.show(
                                    title = errorPopupTitle,
                                    text = err.message ?: errorPopupTitle
                                )
                            }
                        },
                        content = {
                            TextNormal(
                                stringResource(Res.string.register_account_title)
                            )
                        }
                    )
                }
            }
        }
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.End
        ) {
            CustomOutlinedButton(
                onClick = { model.show(RouterMain.navigateTo()) },
                content = {
                    TextNormal(
                        stringResource(Res.string.cancel)
                    )
                }
            )
        }
    }
}


private data class RegisterState(
    val username: String = "",
    val password: String = "",
    val confirmation: String = "",
    var valid: Boolean = false
) {
    fun newState(
        username: String? = null,
        password: String? = null,
        confirmation: String? = null
    ): RegisterState {
        val newUsername = username ?: this.username
        val newPassword = password ?: this.password
        val newConfirmation = confirmation ?: this.confirmation

        return copy(
            username = newUsername,
            password = newPassword,
            confirmation = newConfirmation,
            valid = newUsername.isNotBlank() &&
                    newPassword.isNotBlank() &&
                    newPassword == newConfirmation
        )
    }
}

@Preview
@Composable
private fun CreateAccountScreenDisplayPreview() {
    PreviewDarkLightColumn(
        submodifier = {
            Modifier
                .fillMaxWidth()
                .weight(1f)
        }
    ) { modifier, _ ->
        CreateAccountScreen(
            loginCaller = object : ILoginCaller {
                override fun register(
                    email: String,
                    password: String,
                    callback: (err: Throwable?) -> Unit
                ) = Unit

                override fun login(
                    email: String,
                    password: String,
                    callback: (err: Throwable?) -> Unit
                ) = Unit
            },
            modifier = modifier
        )
    }
}
